import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type FormsQuestionOption = {
  value: string;
  label: string;
  score?: number;
};

export type FormsQuestion = {
  id: string;
  text: string;
  type: string;
  category_id?: string;
  options?: FormsQuestionOption[];
  scaleMax?: number;
  scoring?: { method?: string; weight?: number; max_score?: number };
};

export type FormsScoringConfig = {
  categories?: Array<{
    category_id: string;
    name: string;
    color?: string;
    calculation_method?: 'average' | 'sum';
  }>;
};

export type FormsAssignmentDetail = {
  assignment_id: string;
  status: string;
  completed_at: string | null;
  created_at: string | null;
  admin_url: string;
  questionnaire: {
    name: string;
    description: string | null;
    questions: FormsQuestion[];
    scoring_config: FormsScoringConfig | null;
  } | null;
  respondent: {
    first_name: string;
    last_name: string;
  } | null;
  responses: Record<string, unknown>;
  score_details: unknown;
};

async function callAssignmentDetailEF(
  clinicId: string,
  formsAssignmentId: string,
): Promise<FormsAssignmentDetail> {
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) throw new Error('Not authenticated');

  const url = new URL(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/forms-get-assignment-responses`,
  );
  url.searchParams.set('clinic_id', clinicId);
  url.searchParams.set('forms_assignment_id', formsAssignmentId);

  const resp = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const json: { ok: boolean; data?: FormsAssignmentDetail; error?: { message?: string } } =
    await resp.json();
  if (!json.ok || !json.data) {
    throw new Error(json.error?.message ?? 'forms-get-assignment-responses returned error');
  }
  return json.data;
}

export function useFormsAssignmentDetail(
  clinicId: string | null | undefined,
  formsAssignmentId: string | null | undefined,
  enabled = true,
) {
  return useQuery<FormsAssignmentDetail, Error>({
    queryKey: ['forms', 'assignment', 'detail', clinicId ?? null, formsAssignmentId ?? null],
    enabled: Boolean(clinicId && formsAssignmentId && enabled),
    staleTime: 2 * 60 * 1000,
    queryFn: () => callAssignmentDetailEF(clinicId!, formsAssignmentId!),
  });
}
